import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { Moon, Sun } from 'lucide-react'
import { cn } from '@/lib/utils'

export const THEME_KEY = 'artdoors-theme'

interface ThemeToggleProps {
    className?: string
}

const applyTheme = (light: boolean) => {
    const root = document.documentElement

    root.classList.toggle('light', light)
    root.classList.toggle('dark', !light)
}

const ThemeToggle: FC<ThemeToggleProps> = ({ className }): ReactNode => {
    const [light, setLight] = useState(false)

    useEffect(() => {
        setLight(document.documentElement.classList.contains('light'))
    }, [])

    const toggle = () => {
        const next = !light

        setLight(next)
        applyTheme(next)

        try {
            localStorage.setItem(THEME_KEY, next ? 'light' : 'dark')
        }
        catch {
            // Opslag niet beschikbaar, de keuze geldt dan alleen voor dit bezoek.
        }
    }

    return (
        <button
            type='button'
            role='switch'
            aria-checked={light}
            aria-label='Lichte versie van de website'
            title={light ? 'Donkere versie' : 'Lichte versie'}
            onClick={toggle}
            className={cn('relative inline-flex h-8 w-15 shrink-0 items-center rounded-full bg-neutral-800 p-1 ring-1 ring-white/15 transition-colors focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none', light && 'bg-neutral-200 ring-black/10', className)}
        >
            <Moon className={cn('absolute left-2 size-3.5 transition-opacity', light ? 'text-neutral-500 opacity-60' : 'opacity-0')} aria-hidden='true' />
            <Sun className={cn('absolute right-2 size-3.5 transition-opacity', light ? 'opacity-0' : 'text-neutral-400 opacity-60')} aria-hidden='true' />
            <span className={cn('flex size-6 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition-transform duration-300', light && 'translate-x-7')}>
                {light ? <Sun className='size-3.5' aria-hidden='true' /> : <Moon className='size-3.5' aria-hidden='true' />}
            </span>
        </button>
    )
}

export default ThemeToggle
